/**
 * What a crash does to how a vehicle drives.
 *
 * Each wheel is bent by the crushes near its hub (`wheelBends`): pushed back
 * or in by the metal round it, its suspension arm bent and its toe knocked
 * out. From that, and how far gone the engine's end is (`handlingOf`):
 *
 * - **Power.** A crushed front breaks the radiator and bends the engine's
 *   mountings: it loses power and smokes, steam first, then oil. Past
 *   nearly the whole of the crumple zone it will not start. On a truck the
 *   engine sits under the cab, at the front too; on a motorbike it is in the
 *   middle, and either side's crush reaches it.
 * - **Pull.** A front wheel knocked out of line steers the vehicle toward its
 *   side, as a bent track rod does; a bent rear wheel less so. Bent forks do
 *   it to a motorbike, and set it weaving at speed.
 * - **Drag and grip.** A wheel pushed back into its arch rubs: drag, a lower
 *   top speed, and less grip, with the tyre half off the road.
 * - **Broken.** A wheel pushed in or back over a hand's span has its
 *   suspension torn: it will not drive.
 */

import type { Build, Crush, Vec3, VehicleDamage } from './damage.js';
import { crushField } from './damage.js';

/** A wheel, in the vehicle's frame (x right, z back): where its hub is, and whether it steers. */
export interface WheelSpot {
  x: number;
  /** The hub's height, metres: the wheel's radius. */
  y: number;
  z: number;
  front: boolean;
}

/** How a wheel has been knocked: how far its hub moved (vehicle frame) and its toe, radians, out at the front positive. */
export interface Bend {
  x: number;
  y: number;
  z: number;
  toe: number;
}

/** Most a hub moves: the suspension arm folds no further, metres. */
const MOST = 0.35;

/** How each wheel has been knocked by the crushes, in order. */
export function wheelBends(crushes: readonly { crush: Crush }[], wheels: readonly WheelSpot[]): Bend[] {
  const field: Vec3 = [0, 0, 0];
  return wheels.map((w) => {
    let x = 0, y = 0, z = 0;
    for (const { crush } of crushes) {
      crushField(crush, w.x + x, w.y + y, w.z + z, field);
      x += field[0]; y += field[1]; z += field[2];
    }
    const len = Math.hypot(x, y, z);
    if (len > MOST) { x *= MOST / len; y *= MOST / len; z *= MOST / len; }
    // A hub pushed back on one side of its arm swings the wheel's front out
    // or in: toe. Pushed in toward the middle, it leans in at the top.
    const inward = -Math.sign(w.x) * x;
    const toe = Math.max(-0.35, Math.min(0.35, z * 1.4 * Math.sign(w.x) + inward * 0.6));
    return { x, y: Math.min(0, y), z, toe };
  });
}

/** How a vehicle drives now. Each 1 is as new. */
export interface Handling {
  /** Share of its engine's pull it still has. */
  power: number;
  /** Share of its top speed it can still reach. */
  topSpeed: number;
  /** Share of its tyres' grip. */
  grip: number;
  /** Rolling drag, times as new. */
  drag: number;
  /** Steering it drifts to by itself, as a share of full lock: positive right. */
  pull: number;
  /** How hard it weaves at speed, a motorbike on bent forks: 0 none. */
  wobble: number;
  /** Smoke from under the bonnet, 0 to 1: steam, then black. */
  smoke: number;
  /** Why it will not drive, if it will not. */
  dead: 'engine' | 'wheel' | undefined;
}

export const AS_NEW: Handling = { power: 1, topSpeed: 1, grip: 1, drag: 1, pull: 0, wobble: 0, smoke: 0, dead: undefined };

const smoothstep = (a: number, b: number, x: number) => {
  const t = Math.max(0, Math.min(1, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

/** Past this share of its crumple zone, metres crushed over the most it takes, an engine will not start. */
export const ENGINE_DEAD = 0.92;
/** A wheel's hub moved this far, metres, has its suspension torn: it will not drive. */
export const WHEEL_TORN = 0.24;

/** How a vehicle of `build` with `damage` drives, its wheels at `wheels`. */
export function handlingOf(damage: VehicleDamage | undefined, build: Build, wheels: readonly WheelSpot[]): Handling {
  if (damage === undefined || damage.crushes.length === 0) return { ...AS_NEW };
  const z = damage.zones;
  // Where the engine is: the front of a car or a cab, the middle of a bike.
  const engine = build === 'moto' ? Math.max(z.front * 0.7, z.left, z.right) : z.front;
  const bends = wheelBends(damage.crushes, wheels);
  const amount = bends.map(b => Math.hypot(b.x, b.z));
  const worst = Math.max(0, ...amount);
  // Toe out of line steers it: a front wheel's toe as a share of full lock, a rear's half.
  let pull = 0;
  bends.forEach((b, i) => {
    // A motorbike's wheels are on its middle line: bent forks pull the way they were pushed.
    const toward = wheels[i].x !== 0 ? Math.sign(wheels[i].x) : Math.sign(b.x);
    // Toe out at the front on the right steers right; the wheel drags its side back.
    pull += (wheels[i].front ? 1 : 0.4) * (toward * Math.abs(b.toe) * 1.6 + toward * amount[i] * 0.8);
  });
  const rub = smoothstep(0.06, 0.22, worst);
  const handling: Handling = {
    power: 1 - 0.85 * smoothstep(0.3, ENGINE_DEAD, engine),
    topSpeed: Math.min(1 - 0.55 * smoothstep(0.3, ENGINE_DEAD, engine), 1 - 0.6 * rub),
    grip: 1 - 0.4 * rub,
    drag: 1 + amount.reduce((s, a) => s + smoothstep(0.03, 0.2, a) * 2.5, 0),
    pull: Math.max(-0.45, Math.min(0.45, pull)),
    wobble: build === 'moto' ? smoothstep(0.03, 0.15, Math.max(0, ...amount.filter((_, i) => wheels[i].front))) : 0,
    smoke: smoothstep(0.3, 0.85, engine),
    dead: engine >= ENGINE_DEAD ? 'engine' : worst >= WHEEL_TORN ? 'wheel' : undefined,
  };
  return handling;
}
